import { useRef } from 'react';
import { Upload, FileText, Trash2, Sparkles, Eye, Loader2, AlertCircle, Check } from 'lucide-react';

const STATUT_OPTIONS = [
  { value: 'na', label: 'N/A', color: 'bg-slate-100 text-slate-600 border-slate-300' },
  { value: 'demande', label: 'Demandé', color: 'bg-orange-50 text-orange-700 border-orange-300' },
  { value: 'recu', label: 'Reçu', color: 'bg-green-50 text-green-700 border-green-300' },
];

const ANALYSE_BADGES = {
  none: null,
  pending: { icon: Loader2, label: 'Analyse...', className: 'text-blue-600 animate-spin' },
  done: { icon: Check, label: 'Analysé', className: 'text-green-600' },
  error: { icon: AlertCircle, label: 'Erreur', className: 'text-red-600' },
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function DocumentRow({
  document,
  label,
  uploading,
  analyzing,
  onStatusChange,
  onUpload,
  onDeleteFile,
  onAnalyze,
  onViewAnalysis,
}) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
      e.target.value = '';
    }
  };

  const analyseBadge = ANALYSE_BADGES[document.analyse_statut];

  return (
    <div className="flex items-center gap-3 py-3 px-4 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors">
      {/* Label */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-900 truncate">{label}</span>
        </div>
      </div>

      {/* Tri-state toggle */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {STATUT_OPTIONS.map(({ value, label: statusLabel, color }) => (
          <button
            key={value}
            type="button"
            onClick={() => onStatusChange(value)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
              document.statut === value
                ? color
                : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300 hover:text-slate-500'
            }`}
          >
            {statusLabel}
          </button>
        ))}
      </div>

      {/* File zone */}
      <div className="flex items-center gap-2 flex-shrink-0 min-w-[200px] justify-end">
        {document.filename_original ? (
          <>
            <span className="text-xs text-slate-500 truncate max-w-[120px]" title={document.filename_original}>
              {document.filename_original}
            </span>
            <span className="text-xs text-slate-400">
              {formatFileSize(document.taille_octets)}
            </span>
            <button
              type="button"
              onClick={onDeleteFile}
              className="p-1 text-slate-400 hover:text-red-500 transition-colors"
              title="Supprimer le fichier"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 hover:border-slate-300 transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              Charger
            </button>
          </>
        )}
      </div>

      {/* Analysis zone */}
      <div className="flex items-center gap-1.5 flex-shrink-0 min-w-[100px] justify-end">
        {document.filename_original && document.analyse_statut === 'none' && (
          <button
            type="button"
            onClick={onAnalyze}
            disabled={analyzing}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-carex-600 bg-carex-50 border border-carex-200 rounded-md hover:bg-carex-100 transition-colors disabled:opacity-50"
          >
            {analyzing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Analyser
          </button>
        )}
        {analyseBadge && (
          <button
            type="button"
            onClick={document.analyse_statut === 'done' ? onViewAnalysis : undefined}
            className={`flex items-center gap-1 text-xs font-medium ${
              document.analyse_statut === 'done' ? 'cursor-pointer hover:underline' : 'cursor-default'
            }`}
          >
            <analyseBadge.icon className={`h-3.5 w-3.5 ${analyseBadge.className}`} />
            <span className={analyseBadge.className.replace('animate-spin', '')}>
              {analyseBadge.label}
            </span>
            {document.analyse_statut === 'done' && (
              <Eye className="h-3.5 w-3.5 text-slate-400 ml-0.5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
